import './poultryDesign.css';
import Button from '../shared/Button.js';
// BroilerBatchPage — URL-driven record page for one broiler batch.
//
// Routes:
//   /broiler/batches/<encodedBatchName>
//
// Identity: batch.name (preserves existing Activity entityId contract).
//
// Mounts BatchForm in embedded mode: the parent renders Header + back link +
// data-record-title, and BatchForm renders its form body without its own
// Header or full-screen overlay. RecordCollaborationSection is mounted
// below. Direct URL open does not depend on showForm already being true —
// the page calls openEdit(batch) on mount to populate BatchesContext form
// state, and App skips the top-level showForm branch when the URL
// matches /broiler/batches/<id>.
import React, {type ReactNode} from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {sb} from '../lib/supabase.js';
import {fmtDate as fmt} from '../lib/dateUtils.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
import {loadBroilerBatchWeekAverages} from '../lib/broiler.js';
import {calcTimeline, calcBroilerStatsFromDailys, getBatchDisplayColor} from '../lib/broiler.js';
import type {BroilerDailyRow} from '../lib/broiler.js';
import {EquationStrip} from '../shared/StatStrip.js';
import {GroupDot, PhaseBar} from '../shared/GroupPillPicker.js';
import {todayISO} from '../lib/dateUtils.js';
import type {BroilerBatch} from '../lib/broiler.js';
import type {RecordSeqEntry} from '../lib/recordSequence.js';
import {processingStatusLabel} from '../lib/processingStatusDisplay.js';
import {BROILER_PROCESSOR, bookedWith} from '../lib/processingDateBooking.js';
import {navigateToProcessingRoute, processingSourceRoute} from '../lib/processingNav.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use
// eslint-disable-next-line no-unused-vars -- JSX-only use
import BatchForm from './BatchForm.tsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
/* eslint-disable no-unused-vars -- shell primitives are used in JSX only */
import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageNotFound,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
/* eslint-enable no-unused-vars */
import {useBatches} from '../contexts/BatchesContext.jsx';
import {useAuth} from '../contexts/AuthContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {BroilerBatchesHubData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import InlineNotice from '../shared/InlineNotice.jsx';
import Header from '../shared/Header.tsx';

type BroilerBatchRecord = BroilerBatch & {
  id: string;
  name: string;
  breed?: string | null;
  status?: string | null;
  hatchery?: string | null;
  hatchDate?: string | null;
  processingDate?: string | null;
};
type BroilerBatchPageProps = {
  upd: (key: string, value: string) => void;
  closeForm: () => Promise<boolean>;
  submit: (force: boolean) => Promise<boolean>;
  del: (id: string, onDeleted?: () => void) => void;
  openEdit: (batch: unknown) => void;
  parseProcessorXlsx: (file: File) => Promise<void>;
  // NOT `persist`. The embedded form's only write of the ppp-v4 blob is the
  // document upload/remove path, and it names the row it edits instead of
  // rebuilding the array — so this page forwards the narrow saver and neither
  // it nor BatchForm can hand `persist` a short array. `persist` itself is
  // still in BroilerListProps, because BroilerListHub's two status flips call
  // it; it simply never reaches the form.
  persistDocuments: (batchId: string | null, documents: unknown[]) => Promise<boolean>;
};
type WeekAverages = {week4Lbs: number | null; week6Lbs: number | null};
type BroilerLocation = {pathname: string; hash: string; state?: {recordSeq?: RecordSeqEntry[]}};
const BROILER_BATCHES_HUB_PARAMS = Object.freeze({});
const NO_BROILER_DAILY_ROWS: BroilerDailyRow[] = [];
const NO_BROILER_BATCH_ROWS: BroilerBatchRecord[] = [];

function broilerBreedLabel(code: string | null | undefined) {
  if (code === 'CC') return 'Cornish Cross · CC';
  if (code === 'WR') return 'White Ranger · WR';
  return code || '';
}

function BroilerMetaItem({label, value, accent = false}: {label: string; value?: ReactNode; accent?: boolean}) {
  if (!value || value === '—') return null;
  return (
    <span style={{display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, minWidth: 0}}>
      {accent && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: '#C7920A',
            flex: '0 0 auto',
          }}
        />
      )}
      <span style={{color: 'var(--ink-muted)'}}>{label}</span>
      <span style={{color: 'var(--text-primary)', fontWeight: 700, minWidth: 0}}>{value}</span>
    </span>
  );
}

export default function BroilerBatchPage({
  upd,
  closeForm,
  submit,
  del,
  openEdit,
  parseProcessorXlsx,
  persistDocuments,
}: BroilerBatchPageProps) {
  const navigate = useNavigate();
  const location = useLocation() as BroilerLocation;
  const {editId, setShowForm, setBatches} = useBatches();
  const {authState} = useAuth();
  const hubResource = useRouteResource(RESOURCE.BROILER_BATCHES_HUB, BROILER_BATCHES_HUB_PARAMS);
  const hubData = hubResource.data as BroilerBatchesHubData | null;
  const batches = (hubData?.batches as BroilerBatchRecord[] | undefined) ?? NO_BROILER_BATCH_ROWS;
  const broilerDailys = (hubData?.broilerDailys as BroilerDailyRow[] | undefined) ?? NO_BROILER_DAILY_ROWS;
  // App retains the form action closures. Publish the resolved route data into
  // their context mirror before paint without reading that mirror back here.
  React.useLayoutEffect(() => {
    if (hubData) setBatches(batches);
  }, [batches, hubData, setBatches]);

  const encodedName = location.pathname.slice('/broiler/batches/'.length);
  const batchName = (() => {
    try {
      return decodeURIComponent(encodedName);
    } catch (_e) {
      return encodedName;
    }
  })();

  // Pin the resolved batch by id after the initial name lookup so the page
  // survives a name edit (rename autosaves into batches; URL still encodes
  // the original name). Reset whenever the URL batch name changes so
  // navigating between records via prev/next still works.
  const [pinnedId, setPinnedId] = React.useState<string | null>(null);
  const [weighInWeekAverages, setWeighInWeekAverages] = React.useState<WeekAverages | null>(null);
  React.useEffect(() => {
    setPinnedId(null);
  }, [batchName]);

  const batch = React.useMemo<BroilerBatchRecord | null>(() => {
    const list = (batches || []) as BroilerBatchRecord[];
    if (pinnedId) {
      const byId = list.find((b) => b.id === pinnedId);
      if (byId) return byId;
    }
    // A link built from a batch ID (an Activity row, a comment notification or
    // a mention email since migration 236) resolves too; the URL-sync effect
    // below then shows the name. The name is tried first, so a legacy link opens
    // exactly what it always did.
    return list.find((b) => (b.name || '') === batchName) || list.find((b) => !!b.id && b.id === batchName) || null;
  }, [batches, batchName, pinnedId]);
  const batchWeightLookupName = batch ? batch.name || '' : '';

  React.useEffect(() => {
    if (batch && pinnedId !== batch.id) setPinnedId(batch.id);
  }, [batch, pinnedId]);

  React.useEffect(() => {
    let cancelled = false;
    setWeighInWeekAverages(null);
    if (!batchWeightLookupName) {
      return () => {
        cancelled = true;
      };
    }
    loadBroilerBatchWeekAverages(sb, batchWeightLookupName)
      .then((result) => {
        if (cancelled) return;
        if (result && result.ok) {
          setWeighInWeekAverages({week4Lbs: result.week4Lbs, week6Lbs: result.week6Lbs});
          return;
        }
        setWeighInWeekAverages(null);
      })
      .catch(() => {
        if (!cancelled) setWeighInWeekAverages(null);
      });
    return () => {
      cancelled = true;
    };
  }, [batchWeightLookupName]);

  // Populate BatchesContext form state from the URL-resolved batch on mount
  // and whenever the URL batch identity changes. Once editId matches the
  // resolved batch.id, treat the form as dirty-tolerant: subsequent
  // divergence of form.name from batch.name is normal in-progress edits,
  // not a signal to replay openEdit. Replaying would clobber the user's
  // keystrokes (Codex P1 fix).
  React.useEffect(() => {
    if (!batch) return;
    if (editId === batch.id) return;
    openEdit(batch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch && batch.id]);

  // If batches finishes loading and the URL batch is gone, ensure showForm
  // is false so the top-level guard doesn't get tripped by stale state.
  React.useEffect(() => {
    if (!batch && Array.isArray(batches) && batches.length > 0) {
      setShowForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch, batches]);

  // After a name rename autosaves into batches, batch.name diverges from
  // the URL-encoded batchName. Update the URL silently so /broiler/batches/
  // <oldName> doesn't 404 on refresh.
  React.useEffect(() => {
    if (!batch) return;
    const currentName = batch.name || '';
    if (!currentName) return;
    if (currentName === batchName) return;
    navigate('/broiler/batches/' + encodeURIComponent(currentName), {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch && batch.name]);

  // Unmount cleanup: leaving the record route (Header nav, browser Back,
  // anything that swaps BroilerBatchPage out) must clear showForm so the
  // top-level if (showForm) guard does not capture the next render with the
  // full-screen BatchForm.
  React.useEffect(() => {
    return () => {
      setShowForm(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (location.hash) {
      const anchor = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 200);
    }
  }, [location.hash, batch]);

  async function handleClose() {
    // closeForm may refuse (blank name / hard conflict without override) —
    // in that case it returns false, keeps the form open, and surfaces a
    // formNotice. Do not navigate away or clear showForm; the user must
    // resolve the validation stop first.
    const ok = await closeForm();
    if (ok === false) return;
    setShowForm(false);
    navigate('/broiler/batches');
  }

  function handleDeleted() {
    setShowForm(false);
    navigate('/broiler/batches');
  }

  // Prev/Next now flows through the shared RecordSequenceNav. The visible-order
  // sequence (batch ids + names) is carried in route state; resolve the target
  // id to its current name before routing, and carry the sequence forward.
  const recordSeq = location.state?.recordSeq;
  function navigateToBatchId(id: unknown) {
    const target = ((batches || []) as BroilerBatchRecord[]).find((b) => b.id === id);
    if (!target || !target.name) return;
    navigate('/broiler/batches/' + encodeURIComponent(target.name), recordSeqNavOptions(recordSeq));
  }

  if (hubResource.loading) {
    return <RecordPageLoading Header={Header} />;
  }

  if (hubResource.error) {
    return (
      <RecordPageFrame Header={Header}>
        <RecordPageBody maxWidth={960} style={{padding: '24px 24px 96px'}}>
          <RecordBackLink label="Back to Broiler Batches" onBack={handleClose} />
          <InlineNotice
            notice={{
              kind: 'error',
              message: 'Could not load this broiler batch. Please try again. (' + String(hubResource.error) + ')',
            }}
          />
          <Button type="button" onClick={() => void hubResource.refetch()} style={{marginTop: 10}}>
            Try again
          </Button>
        </RecordPageBody>
      </RecordPageFrame>
    );
  }

  if (!batch) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Broiler Batches"
        onBack={handleClose}
        message="Batch not found."
      />
    );
  }

  // Wait for context form state to catch up to the URL batch before mounting
  // BatchForm. openEdit fires above; the next render has editId === batch.id.
  // Do NOT gate on form.name matching batch.name — dirty in-progress edits
  // are normal and must not unmount the form (Codex P1 fix).
  const formReady = editId === batch.id;

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody maxWidth={960} style={{padding: '24px 24px 96px'}} data-broiler-batch-record-loaded="true">
        <RecordBackLink label="Back to Broiler Batches" onBack={handleClose} />

        <RecordSequenceNav seq={recordSeq} currentId={batch.id} onNavigate={navigateToBatchId} />

        <div style={{display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 8}}>
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 10}}>
            <GroupDot color={getBatchDisplayColor(batch).bg} size={10} />
            <RecordTitle
              fontSize={28}
              margin={0}
              style={{fontWeight: 800, letterSpacing: 0, lineHeight: 1, color: 'var(--text-primary)'}}
            >
              {batch.name}
            </RecordTitle>
          </span>
          {/* Lifecycle status → Badge, L18 tones: Planned neutral, In Process info, Complete ok. */}
          <Badge
            status={processingStatusLabel(batch.status)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '5px 13px',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: 'currentColor',
                flex: '0 0 auto',
              }}
            />
            {processingStatusLabel(batch.status)}
          </Badge>
        </div>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px 26px', marginBottom: 22}}>
          <BroilerMetaItem label="Breed" value={broilerBreedLabel(batch.breed)} accent />
          <BroilerMetaItem label="Hatchery" value={batch.hatchery} />
          <BroilerMetaItem label="Hatched" value={fmt(batch.hatchDate)} />
          {/* Every broiler date is booked with Atlanta (Ronnie, 2026-10-08; Q14-A). */}
          <BroilerMetaItem
            label="Processing date"
            value={
              batch.processingDate ? (
                <>
                  {fmt(batch.processingDate)}{' '}
                  <span style={{color: 'var(--ink-muted)', fontWeight: 600}} data-broiler-processing-booked>
                    {bookedWith(BROILER_PROCESSOR)}
                  </span>
                </>
              ) : undefined
            }
          />
          {/* Deep link into the Processing Calendar record for this batch.
              source key is batch.id (immutable), NOT the URL's batch name. */}
          {batch.processingDate && (
            <>
              <Button
                type="button"
                data-processing-source-link="broiler"
                onClick={() => navigateToProcessingRoute(navigate, processingSourceRoute('broiler', batch.id))}
              >
                View in Processing →
              </Button>
            </>
          )}
        </div>

        {/* R16: the batch reads as Started − Mortality = On Farm, with the
            result in the batch's own group tint. R23: the schedule is one bar
            in that colour. Both read what the dailys already say. */}
        {(() => {
          const groupHue = getBatchDisplayColor(batch).bg;
          const stats = calcBroilerStatsFromDailys(batch, broilerDailys);
          const tl = calcTimeline(
            batch.hatchDate,
            batch.breed,
            batch.processingDate,
            batch.growoutWeeks,
            batch.brooderDays,
          );
          const today = todayISO();
          const days = (from: string, to: string) =>
            Math.max(
              1,
              Math.round((new Date(to + 'T12:00:00').getTime() - new Date(from + 'T12:00:00').getTime()) / 86400000),
            );
          const phaseState = (from: string, to: string): 'past' | 'current' | 'done' =>
            today >= to ? 'past' : today >= from ? 'current' : 'done';
          const span = tl ? days(tl.brooderIn, tl.schoonerOut) : 0;
          const elapsed = tl ? days(tl.brooderIn, today) : 0;
          return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 22}}>
              <EquationStrip
                rootAttrs={{'data-broiler-bird-equation': '1'}}
                terms={[
                  {label: 'Started', value: stats.startedBirds > 0 ? stats.startedBirds.toLocaleString() : null},
                  {
                    label: 'Mortality',
                    value: stats.mortality.toLocaleString(),
                    operator: '−',
                    sub: stats.startedBirds > 0 ? stats.mortPct.toFixed(1) + '%' : '',
                  },
                  {
                    label: 'On Farm',
                    value: stats.projectedBirds.toLocaleString(),
                    operator: '=',
                    attrs: {'data-broiler-on-farm': '1'},
                  },
                ]}
              />
              {tl && (
                <PhaseBar
                  color={groupHue}
                  todayAt={span > 0 ? Math.min(1, Math.max(0, elapsed / span)) : null}
                  phases={[
                    {
                      name: 'Brooder',
                      weight: days(tl.brooderIn, tl.brooderOut),
                      state: phaseState(tl.brooderIn, tl.brooderOut),
                      sub: fmt(tl.brooderIn) + ' — ' + fmt(tl.brooderOut),
                    },
                    {
                      name: 'Schooner',
                      weight: days(tl.schoonerIn, tl.schoonerOut),
                      state: phaseState(tl.schoonerIn, tl.schoonerOut),
                      sub: fmt(tl.schoonerIn) + ' — ' + fmt(tl.schoonerOut),
                    },
                  ]}
                />
              )}
            </div>
          );
        })()}

        {formReady && (
          <BatchForm
            upd={upd}
            closeForm={closeForm}
            submit={submit}
            del={del}
            openEdit={openEdit}
            parseProcessorXlsx={parseProcessorXlsx}
            persistDocuments={persistDocuments}
            weighInWeekAverages={weighInWeekAverages}
            onClose={handleClose}
            onDeleted={handleDeleted}
            embedded
          />
        )}

        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="broiler.batch"
          entityId={batch.id}
          entityLabel={batch.name}
        />
      </RecordPageBody>
    </RecordPageFrame>
  );
}
